/**
 * A synchronous, single-flight guard for a submit handler.
 *
 * Why a plain boolean and not React `useState`: a `useState` flag cannot block a *synchronous*
 * double-click. Two rapid native clicks run their onClick handlers within the SAME render, and a
 * `setState(true)` from the first does not update the state variable the second click reads (React
 * batches and re-renders asynchronously) — so both pass the `if (flag) return` check. This guard
 * holds its state in a closure variable that flips the instant `tryAcquire()` runs, so the second
 * synchronous call sees it immediately. Hold one per component via `useRef(createSubmitGuard())`.
 *
 * BE#372/#231: without this, a fast double-click on Schedule Send fired the create twice → two
 * parallel recurring roots (duplicate sends). Pair it with the create carrying a stable
 * clientIdempotencyKey (stamped at dialog-open) so a click that still slips through dedupes server-side.
 */
export interface SubmitGuard {
    /** Returns true and locks on the first call; returns false while locked. */
    tryAcquire(): boolean;
    /** Unlock so the handler can run again (e.g. when the dialog stays open after a no-op branch). */
    release(): void;
    /** Whether a submit is currently in flight (drives the visual disabled affordance if wanted). */
    readonly inFlight: boolean;
}

export function createSubmitGuard(): SubmitGuard {
    let locked = false;
    return {
        tryAcquire() {
            if (locked) return false;
            locked = true;
            return true;
        },
        release() {
            locked = false;
        },
        get inFlight() {
            return locked;
        },
    };
}
